import { useEffect, useState } from "react";
import {
	Alert,
	Button,
	LinearProgress,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Typography,
} from "@mui/material";
import type { RollCutDetail, RollCutHistory as History } from "./models";
import { getRollCutHistory } from "./repository";

function CutList({
	side,
	name,
	cuts,
}: {
	side: string;
	name: string | null;
	cuts: RollCutDetail[];
}) {
	return (
		<Stack gap={1}>
			<Typography variant="body2" fontWeight={600}>
				{side} · {cuts.length} {cuts.length === 1 ? "cut" : "cuts"}
			</Typography>
			{cuts.length === 0 ? (
				<Typography variant="body2" color="text.secondary">
					{name ? "No recorded cuts." : "Roll not present."}
				</Typography>
			) : (
				<Table size="small" aria-label={`${side} recorded cuts`}>
					<TableHead>
						<TableRow>
							<TableCell sx={{ pl: 0 }}>Date</TableCell>
							<TableCell align="right" sx={{ pr: 0 }}>
								Diameter
							</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{cuts.map((cut) => (
							<TableRow key={cut.id}>
								<TableCell sx={{ pl: 0, whiteSpace: "nowrap" }}>
									{cut.date ?? "Date unavailable"}
								</TableCell>
								<TableCell
									align="right"
									sx={{
										pr: 0,
										fontVariantNumeric: "tabular-nums",
									}}
								>
									{cut.diameter?.toFixed(3) ?? "—"}
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			)}
		</Stack>
	);
}

export function RollCutHistory({
	rollshopName,
	mesName,
}: {
	rollshopName: string | null;
	mesName: string | null;
}) {
	const [history, setHistory] = useState<History | null>(null);
	const [error, setError] = useState("");
	const [attempt, setAttempt] = useState(0);
	useEffect(() => {
		const controller = new AbortController();
		setHistory(null);
		setError("");
		getRollCutHistory(rollshopName, mesName, controller.signal)
			.then((data) => {
				if (!controller.signal.aborted) setHistory(data);
			})
			.catch((reason) => {
				if (!controller.signal.aborted)
					setError(
						reason instanceof Error
							? reason.message
							: "Could not load recorded cuts.",
					);
			});
		return () => controller.abort();
	}, [rollshopName, mesName, attempt]);
	return (
		<TableBody>
			<TableRow
				sx={{
					"& > td": {
						verticalAlign: "top",
						pt: 3,
						borderTop: 2,
						borderColor: "divider",
					},
				}}
			>
				<TableCell>
					<Typography variant="subtitle2">Recorded cuts</Typography>
					<Typography variant="caption" color="text.secondary">
						All dates. Newest first in each system.
					</Typography>
				</TableCell>
				{history ? (
					<>
						<TableCell>
							<CutList
								side="Rollshop"
								name={rollshopName}
								cuts={history.rollshop}
							/>
						</TableCell>
						<TableCell>
							<CutList side="MES" name={mesName} cuts={history.mes} />
						</TableCell>
						<TableCell>
							<Typography variant="caption" color="text.secondary">
								View only
							</Typography>
						</TableCell>
					</>
				) : (
					<TableCell colSpan={3}>
						{error ? (
							<Alert
								severity="warning"
								action={
									<Button onClick={() => setAttempt((value) => value + 1)}>
										Retry
									</Button>
								}
							>
								{error}
							</Alert>
						) : (
							<Stack gap={1} role="status">
								<Typography variant="body2">Loading recorded cuts…</Typography>
								<LinearProgress aria-label="Loading recorded cuts" />
							</Stack>
						)}
					</TableCell>
				)}
			</TableRow>
		</TableBody>
	);
}
